import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package,
  Gift,
  Building2,
  Activity,
  X,
  ChevronRight,
  Shield
} from 'lucide-react';
import { BUILD_INFO } from '@/buildInfo';

/**
 * AdminSidebar - Super admin navigation
 * 
 * RESPONSIBILITIES:
 * - Display admin-specific navigation links
 * - Highlight active route
 * - Mobile responsive (overlay)
 * - Visually distinct (purple accent)
 * 
 * DOES NOT:
 * - Fetch data
 * - Make API calls
 * - Handle permissions (pages handle that)
 */

const adminNavigation = [
  { name: 'Overview', href: '/super-admin-raj-singh', icon: LayoutDashboard },
  { name: 'SMS Packs', href: '/super-admin-raj-singh/sms-packs', icon: Package },
  { name: 'Promo Codes', href: '/super-admin-raj-singh/promo-codes', icon: Gift },
  // Future admin routes (commented for now)
  // { name: 'Businesses', href: '/super-admin-raj-singh/businesses', icon: Building2 },
  // { name: 'Usage & Audit', href: '/super-admin-raj-singh/usage', icon: Activity },
];

export default function AdminSidebar({ isOpen, onClose }) {
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/super-admin-raj-singh') {
      return location.pathname === '/super-admin-raj-singh';
    }
    return location.pathname.startsWith(path);
  };

  const formatBuildTime = (isoString) => {
    try {
      return new Date(isoString).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden" 
          onClick={onClose}
        />
      )}

      {/* Admin Sidebar (Purple accent) */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-purple-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo with Admin Badge */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-purple-200 bg-purple-50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <span className="text-lg font-bold text-gray-900">DAZZA</span>
                <span className="block text-xs text-purple-600 font-medium">Super Admin</span>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="lg:hidden p-1.5 rounded-lg hover:bg-purple-100"
            >
              <X className="w-5 h-5 text-purple-600" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {adminNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => onClose()}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active 
                      ? 'bg-purple-50 text-purple-600 border border-purple-200' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Build Version Footer */}
          <div className="p-4 border-t border-purple-200 bg-purple-50">
            <div className="text-center">
              <p className="text-xs text-purple-600 font-medium mb-1">Admin Panel</p>
              <p className="text-xs text-gray-500">
                v{BUILD_INFO.version} • Build #{BUILD_INFO.buildNumber}
              </p>
              {BUILD_INFO.buildTime && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatBuildTime(BUILD_INFO.buildTime)}
                </p>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
